import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getRewardsSettings, updateRewardsSettings } from "../api/rewards";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import RewardsSetupGuideCard from "../components/rewards/RewardsSetupGuideCard";
import { formatPermissionError } from "../utils/permissionErrorMessages";
import "./MarketingSectionCompact.css";

const toFixedMoney = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
};

const centsToDollars = (cents) => {
  const num = Number(cents || 0);
  if (!Number.isFinite(num)) return 0;
  return num / 100;
};

const dollarsToCents = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return 0;
  return Math.round(num * 100);
};

const TOOLTIP_TEXT = {
  enabled:
    "Turn rewards on or off for this salon. When off, clients won’t earn or redeem points.",
  spend_per_point:
    "How much a client must spend to earn 1 point. Example: $1.00 means every $1 spent earns 1 point.",
  point_value:
    "How much 1 point is worth when redeeming. Example: $0.01 means 100 points = $1 off.",
  referral_bonus_points:
    "Extra points given to the referrer when a new client they referred completes their first closed ticket.",
  min_redeem_points:
    "Clients must have at least this many points to redeem. Set 0 to allow any amount.",
  max_redeem_percent_of_ticket:
    "Limits how much of a ticket can be paid using points. Example: 50 means points can cover up to 50%.",
  redeem_delay_hours:
    "How long newly earned points must wait after a ticket is CLOSED before they can be redeemed. Default 24 hours. Set 0 for instant redeem.",
  earn_rounding:
    "Points must be whole numbers. Floor drops cents (default). Round rounds to nearest. Ceil always rounds up.",
  exclude_gift_cards: "If checked, buying gift cards will not earn points.",
  exclude_tips: "If checked, tips do not earn points.",
  derived_earn_rate: "Calculated from Spend per point.",
  derived_value: "Calculated from Point value.",
  derived_cashback: "Approx cashback = (point value ÷ spend per point) × 100.",
};

export default function RewardsProgram() {
  const navigate = useNavigate();
  const { token, user, role } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({
    enabled: true,
    spend_per_point: "1.00",
    point_value: "0.01",
    referral_bonus_points: 500,
    min_redeem_points: 0,
    max_redeem_percent_of_ticket: 100,
    redeem_delay_hours: 24,
    exclude_gift_cards: true,
    exclude_tips: true,
    earn_rounding: "floor",
  });
  const canSeeGuide =
    user?.is_super_admin ||
    ["owner", "manager", "independent_professional"].includes(
      (role || "").toLowerCase()
    );

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getRewardsSettings(token);
      setForm({
        enabled: Boolean(data?.enabled),
        spend_per_point: toFixedMoney(centsToDollars(data?.spend_per_point_cents)),
        point_value: toFixedMoney(centsToDollars(data?.point_value_cents)),
        referral_bonus_points: Number(data?.referral_bonus_points ?? 0),
        min_redeem_points: Number(data?.min_redeem_points ?? 0),
        max_redeem_percent_of_ticket: Number(
          data?.max_redeem_percent_of_ticket ?? 100
        ),
        redeem_delay_hours: Number(data?.redeem_delay_hours ?? 24),
        exclude_gift_cards: Boolean(data?.exclude_gift_cards),
        exclude_tips: Boolean(data?.exclude_tips),
        earn_rounding: data?.earn_rounding || "floor",
      });
    } catch (err) {
      setError(
        formatPermissionError(err, "Unable to load rewards settings.")
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const spendPerPointCents = useMemo(
    () => Math.max(0, dollarsToCents(form.spend_per_point)),
    [form.spend_per_point]
  );
  const pointValueCents = useMemo(
    () => Math.max(0, dollarsToCents(form.point_value)),
    [form.point_value]
  );
  const cashbackRate = useMemo(() => {
    if (!spendPerPointCents) return 0;
    return (pointValueCents / spendPerPointCents) * 100;
  }, [pointValueCents, spendPerPointCents]);
  const roundingExample = useMemo(() => {
    const exampleSpendCents = 1099;
    const spendPerPoint = spendPerPointCents > 0 ? spendPerPointCents : 100;
    const raw = exampleSpendCents / spendPerPoint;
    return {
      spendPerPoint,
      floor: Math.floor(raw),
      round: Math.round(raw),
      ceil: Math.ceil(raw),
    };
  }, [spendPerPointCents]);
  const roundingExampleText = useMemo(() => {
    const spendLabel = toFixedMoney(centsToDollars(roundingExample.spendPerPoint));
    return `Example: $10.99 eligible spend with $${spendLabel}/point: Floor = ${roundingExample.floor} points, Round = ${roundingExample.round} points, Ceil = ${roundingExample.ceil} points`;
  }, [roundingExample]);

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    const payload = {
      enabled: Boolean(form.enabled),
      spend_per_point_cents: spendPerPointCents,
      point_value_cents: pointValueCents,
      referral_bonus_points: Number(form.referral_bonus_points || 0),
      min_redeem_points: Number(form.min_redeem_points || 0),
      max_redeem_percent_of_ticket: Number(
        form.max_redeem_percent_of_ticket || 0
      ),
      redeem_delay_hours: Number(form.redeem_delay_hours ?? 24),
      exclude_gift_cards: Boolean(form.exclude_gift_cards),
      exclude_tips: Boolean(form.exclude_tips),
      earn_rounding: form.earn_rounding || "floor",
    };
    try {
      await updateRewardsSettings(payload, token);
      setNotice("Rewards settings updated.");
      await loadSettings();
    } catch (err) {
      setError(formatPermissionError(err, "Unable to save rewards settings."));
    } finally {
      setSaving(false);
    }
  };

  const modalText = "text-sm md:text-base";

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="marketingSectionShell">
      <div
        className={`marketingSectionWide marketing-section-medium marketing-section-compact ${modalText}`}
      >
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Rewards Program
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {notice}
              </div>
            )}
            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                Loading rewards settings...
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-6">
                  {canSeeGuide && <RewardsSetupGuideCard />}
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Program Settings
                      </h2>
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(form.enabled)}
                          onChange={(e) =>
                            handleFieldChange("enabled", e.target.checked)
                          }
                          className="h-4 w-4 text-slate-900 border-slate-300 rounded"
                        />
                        <span className="inline-flex items-center gap-2">
                          Enabled
                          <InfoTooltip text={TOOLTIP_TEXT.enabled} label="Enabled" />
                        </span>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                          <span>Spend per point ($)</span>
                          <InfoTooltip
                            text={TOOLTIP_TEXT.spend_per_point}
                            label="Spend per point ($)"
                          />
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={form.spend_per_point}
                          onChange={(e) =>
                            handleFieldChange("spend_per_point", e.target.value)
                          }
                          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                          <span>Point value ($)</span>
                          <InfoTooltip
                            text={TOOLTIP_TEXT.point_value}
                            label="Point value ($)"
                          />
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={form.point_value}
                          onChange={(e) =>
                            handleFieldChange("point_value", e.target.value)
                          }
                          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                          <span>Referral bonus points</span>
                          <InfoTooltip
                            text={TOOLTIP_TEXT.referral_bonus_points}
                            label="Referral bonus points"
                          />
                        </label>
                        <input
                          type="number"
                          step="1"
                          value={form.referral_bonus_points}
                          onChange={(e) =>
                            handleFieldChange("referral_bonus_points", e.target.value)
                          }
                          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                          <span>Minimum redeem points</span>
                          <InfoTooltip
                            text={TOOLTIP_TEXT.min_redeem_points}
                            label="Minimum redeem points"
                          />
                        </label>
                        <input
                          type="number"
                          step="1"
                          value={form.min_redeem_points}
                          onChange={(e) =>
                            handleFieldChange("min_redeem_points", e.target.value)
                          }
                          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                          <span>Max redeem % of ticket</span>
                          <InfoTooltip
                            text={TOOLTIP_TEXT.max_redeem_percent_of_ticket}
                            label="Max redeem % of ticket"
                          />
                        </label>
                        <input
                          type="number"
                          step="1"
                          value={form.max_redeem_percent_of_ticket}
                          onChange={(e) =>
                            handleFieldChange(
                              "max_redeem_percent_of_ticket",
                              e.target.value
                            )
                          }
                          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                          <span>Redeem delay (hours)</span>
                          <InfoTooltip
                            text={TOOLTIP_TEXT.redeem_delay_hours}
                            label="Redeem delay (hours)"
                          />
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={form.redeem_delay_hours}
                          onChange={(e) =>
                            handleFieldChange("redeem_delay_hours", e.target.value)
                          }
                          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                          <span>Earn rounding</span>
                          <InfoTooltip
                            text={TOOLTIP_TEXT.earn_rounding}
                            label="Earn rounding"
                          />
                        </label>
                        <select
                          value={form.earn_rounding}
                          onChange={(e) =>
                            handleFieldChange("earn_rounding", e.target.value)
                          }
                          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        >
                          <option value="floor">Floor</option>
                          <option value="round">Round</option>
                          <option value="ceil">Ceil</option>
                        </select>
                        <div className="text-xs text-slate-500">
                          {roundingExampleText}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(form.exclude_gift_cards)}
                          onChange={(e) =>
                            handleFieldChange("exclude_gift_cards", e.target.checked)
                          }
                          className="h-4 w-4 text-slate-900 border-slate-300 rounded"
                        />
                        <span className="inline-flex items-center gap-2">
                          Exclude gift cards
                          <InfoTooltip
                            text={TOOLTIP_TEXT.exclude_gift_cards}
                            label="Exclude gift cards"
                          />
                        </span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(form.exclude_tips)}
                          onChange={(e) =>
                            handleFieldChange("exclude_tips", e.target.checked)
                          }
                          className="h-4 w-4 text-slate-900 border-slate-300 rounded"
                        />
                        <span className="inline-flex items-center gap-2">
                          Exclude tips
                          <InfoTooltip
                            text={TOOLTIP_TEXT.exclude_tips}
                            label="Exclude tips"
                          />
                        </span>
                      </label>
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {saving ? "Saving..." : "Save Settings"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Derived Values
                    </h3>
                  </div>
                  <div className="space-y-2 text-sm text-slate-700">
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="font-medium">Earn rate</span>
                        <InfoTooltip
                          text={TOOLTIP_TEXT.derived_earn_rate}
                          label="Earn rate"
                        />
                        <span>:</span>
                        <span>
                          ${toFixedMoney(centsToDollars(spendPerPointCents))} spend = 1
                          point
                        </span>
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="font-medium">Value</span>
                        <InfoTooltip
                          text={TOOLTIP_TEXT.derived_value}
                          label="Value"
                        />
                        <span>:</span>
                        <span>
                          1 point = ${toFixedMoney(centsToDollars(pointValueCents))}
                        </span>
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="font-medium">Approx cashback</span>
                        <InfoTooltip
                          text={TOOLTIP_TEXT.derived_cashback}
                          label="Approx cashback"
                        />
                        <span>:</span>
                        <span>{cashbackRate.toFixed(2)}%</span>
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="font-medium">Redeem delay</span>
                        <span>:</span>
                        <span>{Number(form.redeem_delay_hours || 0)} hours</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500">
                    Cashback rate is calculated as (point value / spend per point) * 100.
                  </div>
                  <div className="text-xs text-slate-500">
                    New points are earned when a ticket is CLOSED.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

